import { useEffect, useRef, useState } from "react";
import { Modal, ModalProps } from "@nextui-org/react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import useScreenSize from "../../hooks/useScreenSize";
import useStableViewportHeight from "../../hooks/useStableViewportHeight";

//STYLES
import styles from "./CustomModal.module.scss";

const FULLSCREEN_MODAL_WIDTH = 670;

interface CustomModalProps extends ModalProps {
  customHeader?: React.ReactNode;
  isLargerContent?: boolean;
  disableNativeKeyboardHandling?: boolean;
}

const CustomModal = (props: CustomModalProps) => {
  const {
    isLargerContent,
    children,
    classNames,
    customHeader,
    disableNativeKeyboardHandling,
    ...restProps
  } = props;

  const { base, baseMobile, mobileWrapper, keyboardOpen } = styles;

  const [isLargerThanViewport, setIsLargerThanViewport] = useState(false);
  const [fistRender, setFistRender] = useState(false);

  const { width, isMobile } = useScreenSize();
  const stableViewportHeight = useStableViewportHeight();
  const isNative = Capacitor.isNativePlatform();

  const [nativeKeyboardOpen, setNativeKeyboardOpen] = useState(false);

  const initialHeightRef = useRef<number | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const isKeyboardOpen =
    isMobile &&
    !(isNative && disableNativeKeyboardHandling) &&
    ((initialHeightRef.current !== null &&
      stableViewportHeight < initialHeightRef.current - 50) ||
      nativeKeyboardOpen);

  const isFullScreenWidth = width <= FULLSCREEN_MODAL_WIDTH;

  const modalClassNames = {
    base,
    body: "mx-10 my-0 p-0",
    closeButton: "mt-8 mr-8 p-2",
    footer: "px-10 py-10 justify-start gap-x-4",
    header: "px-10 pt-10",
    ...classNames,
  };

  const modalClassNamesMobile = {
    base: `${baseMobile} rounded-none ${isKeyboardOpen ? keyboardOpen : ""}`,
    body: "px-4 pb-4 py-16 overflow-y-auto",
    closeButton:
      "start-0 end-auto top-0 hover:bg-transparent active:bg-transparent z-[51] p-0",
    footer: "p-4 mb-0 z-50 bg-white",
    header: "p-4 z-50",
    wrapper: `${customHeader ? "" : `mt-[4rem] ${mobileWrapper}`} overflow-y-hidden z-[100]`,
    backdrop: `mt-[4rem] max-h-[calc(100dvh-4rem)] ${width <= 768 ? "bg-white" : ""}`,
    dialog: `ms-0 me-0`,
    ...classNames,
  };

  useEffect(() => {
    const calculateHeight = () => {
      if (elementRef.current && fistRender && !isFullScreenWidth) {
        const element = elementRef.current;

        const offsetHeight = element.offsetHeight;
        const style = window.getComputedStyle(element);
        const marginTop = parseInt(style.marginTop, 10);
        const marginBottom = parseInt(style.marginBottom, 10);
        const heightWithMargins = offsetHeight + marginTop + marginBottom;

        const viewportHeight = window.innerHeight;

        setIsLargerThanViewport(
          isLargerContent || heightWithMargins > viewportHeight,
        );
      }
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, [fistRender, isFullScreenWidth, isLargerContent]);

  useEffect(() => {
    if (restProps.isOpen) {
      setFistRender(true);
      if (isMobile) {
        initialHeightRef.current =
          window.visualViewport?.height ?? window.innerHeight;
      }
    }
  }, [restProps.isOpen, isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    document.documentElement.style.setProperty(
      "--visual-vh",
      `${stableViewportHeight}px`,
    );
  }, [isMobile, stableViewportHeight]);

  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const scrollFocused = () => {
      const el = document.activeElement as HTMLElement;
      if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "instant", block: "center" }),
          150,
        );
      }
    };
    vv.addEventListener("resize", scrollFocused);
    return () => vv.removeEventListener("resize", scrollFocused);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile || !isNative || !restProps.isOpen) return;
    if (disableNativeKeyboardHandling) return;

    type Listener = Awaited<ReturnType<typeof Keyboard.addListener>>;
    let showListener: Listener | undefined;
    let hideListener: Listener | undefined;

    Keyboard.addListener("keyboardWillShow", (info) => {
      setNativeKeyboardOpen(true);
      document.documentElement.style.setProperty(
        "--visual-vh",
        `${window.innerHeight - info.keyboardHeight}px`,
      );
      setTimeout(() => {
        const el = document.activeElement as HTMLElement;
        if (el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)) {
          el.scrollIntoView({ behavior: "instant", block: "center" });
        }
      }, 150);
    }).then((l) => (showListener = l));

    Keyboard.addListener("keyboardWillHide", () => {
      setNativeKeyboardOpen(false);
      document.documentElement.style.setProperty(
        "--visual-vh",
        `${window.innerHeight}px`,
      );
    }).then((l) => (hideListener = l));

    return () => {
      showListener?.remove();
      hideListener?.remove();
    };
  }, [isMobile, isNative, restProps.isOpen]);

  return (
    <>
      {isMobile ? (
        <Modal
          classNames={modalClassNamesMobile}
          className="m-0"
          ref={elementRef}
          motionProps={{
            variants: {
              enter: {
                scale: "var(--scale-enter)",
                y: "var(--slide-enter)",
                opacity: 1,
                willChange: "auto",
                transition: { duration: 0 },
              },
              exit: {
                scale: "var(--scale-exit)",
                y: "var(--slide-exit)",
                opacity: 0,
                willChange: "transform",
                transition: { duration: 0 },
              },
            },
          }}
          {...restProps}
        >
          {children}
        </Modal>
      ) : (
        <Modal
          backdrop="blur"
          scrollBehavior={
            isLargerThanViewport || isFullScreenWidth ? "inside" : "normal"
          }
          classNames={modalClassNames}
          className="m-0"
          ref={elementRef}
          {...restProps}
        >
          {children}
        </Modal>
      )}
    </>
  );
};

export default CustomModal;

//EXAMPLE USAGE:

// <CustomModal isOpen={isOpen} onClose={() => setIsOpen(!isOpen)}>
// <ModalContent>
//   {(onClose) => (
//     <>
//       <ModalHeader>Modal Title</ModalHeader>
//       <ModalBody>
//         <p>
//           Lorem ipsum dolor sit amet, consectetur adipiscing elit.
//           Nullam pulvinar risus non risus hendrerit venenatis.
//           Pellentesque sit amet hendrerit risus, sed porttitor quam.
//         </p>
//         <p>
//           Lorem ipsum dolor sit amet, consectetur adipiscing elit.
//           Nullam pulvinar risus non risus hendrerit venenatis.
//           Pellentesque sit amet hendrerit risus, sed porttitor quam.
//         </p>
//         <p>
//           Magna exercitation reprehenderit magna aute tempor cupidatat
//           consequat elit dolor adipisicing. Mollit dolor eiusmod sunt ex
//           incididunt cillum quis. Velit duis sit officia eiusmod Lorem
//           aliqua enim laboris do dolor eiusmod. Et mollit incididunt
//           nisi consectetur esse laborum eiusmod pariatur proident Lorem
//           eiusmod et. Culpa deserunt nostrud ad veniam.
//         </p>
//         <p>
//           Lorem ipsum dolor sit amet, consectetur adipiscing elit.
//           Nullam pulvinar risus non risus hendrerit venenatis.
//           Pellentesque sit amet hendrerit risus, sed porttitor quam.
//         </p>
//         <p>
//           Lorem ipsum dolor sit amet, consectetur adipiscing elit.
//           Nullam pulvinar risus non risus hendrerit venenatis.
//           Pellentesque sit amet hendrerit risus, sed porttitor quam.
//         </p>
//         <p>
//           Magna exercitation reprehenderit magna aute tempor cupidatat
//           consequat elit dolor adipisicing. Mollit dolor eiusmod sunt ex
//           incididunt cillum quis. Velit duis sit officia eiusmod Lorem
//           aliqua enim laboris do dolor eiusmod. Et mollit incididunt
//           nisi consectetur esse laborum eiusmod pariatur proident Lorem
//           eiusmod et. Culpa deserunt nostrud ad veniam.
//         </p>
//       </ModalBody>
//       <ModalFooter>
//         <Button color="danger" onPress={onClose}>
//           Close
//         </Button>
//         <Button color="primary" onPress={onClose}>
//           Action
//         </Button>
//       </ModalFooter>
//     </>
//   )}
// </ModalContent>
// </CustomModal>
